import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { enrichPatientData } from '../../utils/patientDataEnrichment';
import { calculateMentalState, calculateOverallMentalHealthScore } from '../../utils/mentalStateCalculations';
import { prepareReportChartData, generateEmotionTrendData } from '../../utils/doctorDashboardUtils';
import { EmotionTrendsChart, RiskAnalysisPanel, MentalStateDisplay } from '../DoctorReportWidgets';
import { getAssessmentBadgeClass } from '../../utils/dateFormatting';

export default function ReportView({ patient, sessionHistory, lastSessionNote, isOpen, onClose, onViewHistory }) {
  if (!isOpen || !patient) return null;

  const enrichedPatient = enrichPatientData(patient, sessionHistory);
  const { emotionData, weeklyData, monthlyData } = prepareReportChartData(enrichedPatient, calculateOverallMentalHealthScore);
  const emotionTrendData = generateEmotionTrendData(sessionHistory);
  const mentalState = calculateMentalState(enrichedPatient);

  if (!enrichedPatient.hasSessionData) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content large" onClick={e => e.stopPropagation()}>
          <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2 style={{ margin: 0 }}>Patient Report - {enrichedPatient.name}</h2>
              <button className="btn-secondary" onClick={() => {
                onClose();
                onViewHistory(patient);
              }} style={{ fontSize: '14px', padding: '6px 12px' }}>
                History
              </button>
            </div>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <div className="new-patient-message">
              <div className="new-patient-icon">👤</div>
              <h3>New Patient</h3>
              <p>No session data available yet for {enrichedPatient.name}.</p>
              <p>Reports and analytics will be available after the patient completes their first session.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={e => e.stopPropagation()} style={{ maxWidth: '1200px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ margin: 0 }}>Patient Report - {enrichedPatient.name}</h2>
            <button className="btn-secondary" onClick={() => {
              onClose();
              onViewHistory(patient);
            }} style={{
              fontSize: '14px',
              padding: '6px 12px',
              fontWeight: '600'
            }}>
              History
            </button>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="report-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Emotion Vector Chart */}
            <div className="chart-section radar-card radar-flat" style={{ gridColumn: '1 / -1', position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ margin: 0 }}>Emotion Vector</h3>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {sessionHistory && sessionHistory.length > 0 && sessionHistory[0].emotion?.doctor_assessment && (
                    <div style={{
                      background: '#f9fafb',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      fontSize: '12px'
                    }}>
                      <div style={{ fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Clinical Assessment:</div>
                      <span className={`badge ${getAssessmentBadgeClass(sessionHistory[0].emotion.doctor_assessment)}`} style={{ fontSize: '11px', padding: '4px 8px' }}>
                        {sessionHistory[0].emotion.doctor_assessment}
                      </span>
                    </div>
                  )}
                  {lastSessionNote && (
                    <div style={{
                      maxWidth: '300px',
                      background: '#f9fafb',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      fontSize: '12px'
                    }}>
                      <div style={{ fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Clinical Assessment Note:</div>
                      <div style={{ color: '#6b7280', lineHeight: '1.4', maxHeight: '60px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lastSessionNote}</div>
                    </div>
                  )}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={340}>
                <RadarChart data={emotionData} cx="50%" cy="50%" outerRadius="84%" startAngle={225} endAngle={585}>
                  <PolarGrid gridType="polygon" stroke="#d8d8d8" strokeOpacity={0.6} radialLines={true} />
                  <PolarAngleAxis dataKey="name" tick={{ fill: '#7a7a7a', fontSize: 13, fontWeight: 400 }} tickMargin={10} />
                  <PolarRadiusAxis angle={90} domain={[0, 80]} tick={false} />
                  <Radar name="Emotions" dataKey="value" stroke="rgba(84,120,255,0.9)" strokeWidth={2.5} fill="rgba(111,141,255,0.35)" fillOpacity={0.45} />
                  <Tooltip formatter={(value) => [`${Math.round(value)}%`, '']} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Weekly Report */}
            <div className="chart-section">
              <h3>Weekly Report</h3>
              {weeklyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#6b7280' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} domain={[0, 100]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="score" stroke="#4f46e5" strokeWidth={3} dot={{ r: 6, fill: '#4f46e5' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data-message">No weekly data available</div>
              )}
            </div>

            {/* Monthly Report */}
            <div className="chart-section">
              <h3>Monthly Report</h3>
              {monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6b7280' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} domain={[0, 100]} />
                    <Tooltip />
                    <Bar dataKey="score" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data-message">No monthly data available</div>
              )}
            </div>

            {/* Emotion Trends */}
            <div style={{ gridColumn: '1 / -1' }}>
              <EmotionTrendsChart data={emotionTrendData} style={{ gridColumn: '1 / -1' }} />
            </div>

            {/* Risk Analysis */}
            <div className="risk-analysis" style={{ gridColumn: '1 / -1' }}>
              <h3>Risk Analysis</h3>
              <RiskAnalysisPanel
                riskComponents={enrichedPatient.riskComponents}
                cumulativeRisk={enrichedPatient.cumulativeRisk}
              />
            </div>

            {/* Mental State */}
            <MentalStateDisplay mentalState={mentalState} style={{ gridColumn: '1 / -1' }} />
          </div>
        </div>
      </div>
    </div>
  );
}
